import { RollbackOutlined, SwapOutlined } from '@ant-design/icons';
import { Descriptions, Space, Table, Typography, message } from 'antd';
import axios from 'axios';
import moment from 'moment';
import { useContext, useState } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { CiLocationOn } from 'react-icons/ci';
import { GrMapLocation } from 'react-icons/gr';
import { IoWarningOutline } from 'react-icons/io5';
import { useHistory } from 'react-router-dom';

import ProductImage from '../../../../components/productImage';
import SectionCard from '../../../../components/sectionCard/SectionCard';
import StepFooter from '../../../../components/stepFooter/StepFooter';
import { clearObjFields } from '../../../../utils';
import { isHandledByInterceptor } from '../../../../utils/apiError';
import { formatCurrency } from '../../../../utils/format';
import { AddOrderReturnContext } from '../addOrderReturnContext';
import { clearDraft } from '../draft';

const { Text } = Typography;

/** Барааны зурагны хэмжээ */
const IMAGE_SIZE = 40;

/** Барааны нэрийн хамгийн их өргөн (урт бол ellipsis + tooltip) */
const TITLE_MAX_WIDTH = 320;

/** Хоосон утгыг "-" болгож харуулна */
const val = (value) => (value === null || value === undefined || value === '' ? '-' : value);

/** Descriptions-ийн ерөнхий тохиргоо: label дээр, value доор, зай багатай */
const descriptionProps = {
  size: 'small',
  colon: false,
  layout: 'vertical',
  column: { xs: 1, sm: 2 },
  labelStyle: { paddingBottom: 0 },
  contentStyle: { paddingBottom: 8 },
};

/** Тоог нийлбэрт нэмэхэд NaN гарахаас хамгаална */
const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Мөрийн тоо ширхэг — буцаах бараа `totalUnitReturn`, солих бараа `productTotalUnit` */
const rowUnit = (record) => num(record?.totalUnitReturn ?? record?.productTotalUnit);

const columns = [
  {
    key: 'index',
    title: '№',
    width: 48,
    align: 'center',
    render: (_, record, index) => <Text type="secondary">{index + 1}</Text>,
  },
  {
    key: 'product',
    title: 'Бараа',
    render: (_, record) => (
      <Space size={12} align="center">
        <ProductImage
          src={record?.productImage}
          alt={record?.productTitle ?? 'Барааны зураг'}
          size={IMAGE_SIZE}
          style={{ objectFit: 'contain', borderRadius: 4, border: '1px solid #eaecf0' }}
        />
        <Space direction="vertical" size={0} style={{ maxWidth: TITLE_MAX_WIDTH }}>
          <Text style={{ maxWidth: TITLE_MAX_WIDTH }} ellipsis={{ tooltip: record?.productTitle }}>
            {val(record?.productTitle)}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
            {record?.productSKU}
          </Text>
        </Space>
      </Space>
    ),
  },
  {
    key: 'price',
    title: 'Нэгжийн үнэ',
    align: 'right',
    width: 140,
    render: (_, record) => formatCurrency(num(record?.productPrice)),
  },
  {
    key: 'delivered',
    title: 'Хүргэсэн тоо',
    align: 'right',
    width: 120,
    render: (_, record) => (record?.totalUnit != null ? `${record.totalUnit} ш` : '-'),
  },
  {
    key: 'totalUnit',
    title: 'Тоо ширхэг',
    align: 'right',
    width: 120,
    render: (_, record) => `${rowUnit(record)} ш`,
  },
  {
    key: 'total',
    title: 'Нийт дүн',
    align: 'right',
    width: 160,
    render: (_, record) => (
      <Text strong>{formatCurrency(num(record?.productPrice) * rowUnit(record))}</Text>
    ),
  },
];

/** Хүснэгтийн нийлбэр мөр — тоо ширхэг, нийт дүн */
const renderSummary = (rows = []) => {
  const totalUnit = rows.reduce((total, record) => total + rowUnit(record), 0);
  const totalPrice = rows.reduce(
    (total, record) => total + num(record?.productPrice) * rowUnit(record),
    0
  );

  return (
    <Table.Summary.Row>
      <Table.Summary.Cell index={0} colSpan={4} align="right">
        <Text type="secondary">Нийт</Text>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={4} align="right">
        <Text strong>{`${totalUnit} ш`}</Text>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={5} align="right">
        <Text strong>{formatCurrency(totalPrice)}</Text>
      </Table.Summary.Cell>
    </Table.Summary.Row>
  );
};

const Step3 = ({ prevStep }) => {
  const history = useHistory();
  const [submitting, setSubmitting] = useState(false);

  const { draftKey, selectedOrder, products, formDataStep1, reference, formDataStep2 } =
    useContext(AddOrderReturnContext);

  const returnProducts = products ?? [];
  const exchangeProducts = formDataStep1?.products ?? [];

  const totalPrice = returnProducts.reduce(
    (total, record) => total + num(record?.productPrice) * rowUnit(record),
    0
  );

  const getReturnProducts = () =>
    returnProducts.map((item) => {
      const product = {
        baseSku: item.baseSku,
        productSKU: item.productSKU,
        productTitle: item.productTitle,
        productImage: item.productImage,
        productPrice: item.productPrice,
        productType: item.productType,
        productVolume: item.totalVolume / item.totalUnit,
        productTotalUnit: item.totalUnitReturn,
        productTotalWeight: item.totalUnitReturn * (item.totalWeight / item.totalUnit),
        productMerchantCode: item.requesterCode,
      };
      if (item.expiryDate) product.productExpiryDate = item.expiryDate;
      return product;
    });

  const onCreate = async () => {
    const reqData = {
      returnProducts: getReturnProducts(),
      returnPlace: formDataStep2?.returnPlace,
      returnPhone: selectedOrder?.customerPhone,
      returnEmail: selectedOrder?.customerEmail,
      returnName: selectedOrder?.customerName,
      returnCode: selectedOrder?.code,
      returnDetail: formDataStep2?.formValues?.dropoffDetail,
      returnPositionCode: formDataStep2?.formValues?.deliveryPositionCode,
      dropoffAddressCode: formDataStep2?.formValues?.dropoffAddressCode,
      pickupType: formDataStep2?.formValues?.deliveryType,
      pickupDate: formDataStep2?.pickupDate?.$d,
      pickupTime: formDataStep2?.formValues?.pickupTime,
      pickupAddress: formDataStep2?.recieveAddress,
      dropoffDate: formDataStep2?.receiveDate,
      dropoffTime: formDataStep2?.receiveTime,
      notes: formDataStep2?.returnReason,
      orderDescription: formDataStep2?.returnReasonDescription,
    };

    setSubmitting(true);
    try {
      const { data } = await axios.post(
        '/api/v1/request/create-return-request',
        clearObjFields(reqData)
      );

      if (data.statusCode !== 200) {
        // Interceptor мэдэгдээгүй кодод л локал мэдэгдэл гаргана — эс тэгвээс давхардана
        if (!isHandledByInterceptor(data)) {
          // Серверийн тайлбар урт байж болох тул уншиж амжихаар удаан харуулна
          message.error(
            data?.message_mn ?? data?.message ?? 'Захиалга үүсгэхэд алдаа гарлаа.',
            6
          );
        }
        return;
      }

      // Солих бараа сонгосон бол хамт хүргэлтийн захиалга үүсгэнэ
      if (exchangeProducts.length > 0 && data.result?.code) {
        const deliverBody = {
          ...formDataStep1,
          deliveryType:
            formDataStep2.deliveryType === 'selfReturn' ? 'deliveryselfproclaim' : 'delivery',
          deliveryCode: formDataStep2.address,
          deliveryDetail: formDataStep2?.formValues?.dropoffDetail,
          deliveryPositionCode: formDataStep2?.formValues?.deliveryPositionCode,
          pricingCode: formDataStep1?.values?.pricingCode,
          dropoffDate: formDataStep2?.formValues?.dropoffDate ?? '',
          dropoffTime: formDataStep2?.formValues?.dropoffTime ?? [],
          notes: formDataStep1?.values?.notes ?? [],
          orderDescription: formDataStep2?.formValues?.orderDescription,
          // Хүлээн авагч нь буцаалт хийж буй захиалгын харилцагч өөрөө
          deliveryPhone: selectedOrder?.customerPhone,
          deliveryEmail: selectedOrder?.customerEmail,
          deliveryName: selectedOrder?.customerName,
          serviceTotalAmount: formDataStep1?.values?.totalPrice,
          attachment: formDataStep1?.values?.attachment,
          isReplaced: true,
          replaceOrder: data.result.code,
          warehouseCode:
            formDataStep2?.returnPlace === 'Агуулах' && formDataStep2?.dropoffAddressCode,
        };

        await axios.post('/api/v1/request/create-delivery-request', clearObjFields(deliverBody));
      }

      message.success('Захиалга амжилттай үүслээ');
      // Дараагийн захиалгад хагас дутуу мэдээлэл нь сэргэхгүй байхын тулд
      clearDraft(draftKey);
      history.push('/merchant/order/return');
    } catch (e) {
      // Алдааны мэдэгдлийг axios interceptor аль хэдийн гаргасан — давхардуулахгүй
      console.error('create-return-request: ', e);
    } finally {
      setSubmitting(false);
    }
  };

  const productTable = (dataSource) => (
    <Table
      columns={columns}
      dataSource={dataSource}
      rowKey={(record, index) => record?._id ?? index}
      size="middle"
      pagination={false}
      summary={renderSummary}
      scroll={{ x: 'max-content' }}
    />
  );

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <SectionCard icon={<AiOutlineInfoCircle />} title="Захиалгын мэдээлэл">
        <Descriptions
          {...descriptionProps}
          column={{ xs: 1, sm: 2, lg: 4 }}
          items={[
            { key: 'code', label: 'Захиалга', children: val(selectedOrder?.code) },
            { key: 'name', label: 'Нэр', children: val(selectedOrder?.customerName) },
            { key: 'phone', label: 'Утас', children: val(selectedOrder?.customerPhone) },
            { key: 'email', label: 'И-мэйл', children: val(selectedOrder?.customerEmail) },
          ]}
        />
      </SectionCard>

      <SectionCard
        icon={<RollbackOutlined />}
        title="Буцаах бараа"
        styles={{ body: { padding: 10 } }}
      >
        {productTable(returnProducts)}
      </SectionCard>

      {exchangeProducts.length > 0 && (
        <SectionCard icon={<SwapOutlined />} title="Солих бараа" styles={{ body: { padding: 10 } }}>
          {productTable(exchangeProducts)}
        </SectionCard>
      )}

      <SectionCard icon={<GrMapLocation />} title="Буцаалт">
        <Descriptions
          {...descriptionProps}
          items={[
            {
              key: 'date',
              label: 'Буцаалтын огноо',
              children: formDataStep2?.pickupDate
                ? moment(formDataStep2.pickupDate.$d).format('YYYY/MM/DD')
                : '-',
            },
            {
              key: 'address',
              label: 'Буцаах хаяг',
              children: val(
                reference?.warehouses?.find(
                  (item) => item.code === formDataStep2?.dropoffAddressCode
                )?.name
              ),
            },
            {
              key: 'type',
              label: 'Авах төрөл',
              children: val(
                reference?.deliveryType?.detail?.[formDataStep2?.formValues?.deliveryType]
              ),
            },
            {
              key: 'from',
              label: 'Хаанаас',
              children: formDataStep1?.values?.deliveryPositionCode
                ? `${formDataStep1.values.deliveryPositionCode}, ${val(formDataStep1.cabinetAddress)}`
                : '-',
            },
          ]}
        />
      </SectionCard>

      <SectionCard icon={<CiLocationOn />} title="Хүлээж авах хаяг">
        <Descriptions
          {...descriptionProps}
          column={{ xs: 1, sm: 2, lg: 4 }}
          items={[
            {
              key: 'city',
              label: 'Хот/аймаг',
              children: val(formDataStep2?.recieveAddress?.[0]?.name),
            },
            {
              key: 'district',
              label: 'Дүүрэг/сум',
              children: val(formDataStep2?.recieveAddress?.[1]?.name),
            },
            {
              key: 'khoroo',
              label: 'Хороо/баг',
              children: val(formDataStep2?.recieveAddress?.[2]?.name),
            },
            {
              key: 'detail',
              label: 'Дэлгэрэнгүй хаяг',
              children: val(formDataStep2?.dropoffDetail),
            },
          ]}
        />
      </SectionCard>

      <SectionCard icon={<IoWarningOutline />} title="Нэмэлт">
        <Descriptions
          {...descriptionProps}
          column={1}
          items={[
            {
              key: 'reason',
              label: 'Буцаалтын шалтгаан',
              children: val(reference?.returnReason?.detail?.[formDataStep2?.returnReason]),
            },
            {
              key: 'description',
              label: 'Тэмдэглэл',
              children: (
                <div style={{ overflowWrap: 'break-word' }}>
                  {val(formDataStep2?.returnReasonDescription)}
                </div>
              ),
            },
          ]}
        />
      </SectionCard>

      <StepFooter
        onPrev={prevStep}
        onSubmit={onCreate}
        htmlType="button"
        submitText="Захиалга үүсгэх"
        loading={submitting}
        extra={
          <Text type="secondary">
            Буцаах барааны дүн: <Text strong>{formatCurrency(totalPrice)}</Text>
          </Text>
        }
      />
    </Space>
  );
};

export default Step3;
